import type { JSX } from 'react';
import React from 'react';
import ReactDOM from 'react-dom';
import { MessageModel } from '../types/models';
import { useTTSStore } from '../stores/ttsStore';
import { useToastStore } from '../stores/ToastStore';


interface TTSPlayerModalProps {
  isVisible: boolean;
  onClose: () => void;
  text: string;
  language?: string;
  
  // TTS state
  isPlaying: boolean;
  isLoading: boolean;
  isPaused: boolean;
  hasActive: boolean;
  
  // TTS callbacks
  onPlay: (message: MessageModel, sanitize?: boolean, language?: string) => void;
  onStop: () => void;
  onPause: () => void;
  onResume: () => void;
}

export function TTSPlayerModal({
  onClose,
  text
}: TTSPlayerModalProps): JSX.Element | null {
  const tts = useTTSStore();
  const { toast, isModalOpen } = useToastStore();

  const handleStop = () => {
    tts.stop();
    onClose();
    sessionStorage.removeItem('tts-was-active');
  };
  
  const handleReplay = () => {
    const message: MessageModel = {
      content: text,
      role: 'user',
    };
    
    if (tts.hasCachedAudio() && tts.text === tts.getCachedMessage()) {
      tts.replay();
    } else {
      tts.play(message, true, tts.language);
    }
  };

  // Uncomment for debugging TTS state
  // React.useEffect(() => {
  //   console.log('TTS state:', { 
  //     isPlaying: tts.isPlaying, 
  //     isPaused: tts.isPaused, 
  //     hasActive: tts.hasActive, 
  //     isLoading: tts.isLoading,
  //     showReplay,
  //     text: tts.text?.substring(0, 20) // First 20 chars of text
  //   });
  // }, [tts.isPlaying, tts.isPaused, tts.hasActive, tts.isLoading, showReplay, tts.text]);

  if (!tts.isVisible) return null;

  // Calculate position based on modal and toast visibility
  const isToastShowing = toast !== null;
  // If a modal is open, position to bottom right corner to avoid overlap
  let horizontalPosition: string;
  let transformX: string;
  let verticalPosition: string;
  
  if (isModalOpen) {
    // Bottom right corner
    horizontalPosition = 'auto';
    transformX = '0';
    verticalPosition = '1.5rem';
  } else if (isToastShowing) {
    // Shift left when toast is showing
    horizontalPosition = 'calc(95% - 11rem)';
    transformX = '-100%';
    verticalPosition = '6rem';
  } else {
    // Center when no toast or modal
    horizontalPosition = '50%';
    transformX = '-50%';
    verticalPosition = '6rem';
  }

  return ReactDOM.createPortal(
    <div 
      className="fixed bottom-24 bg-neutral-900 border border-neutral-700 rounded-full shadow-xl transition-all duration-200 z-[9998] flex items-center px-4 py-2"
      style={{ 
        maxWidth: 'min(calc(100% - 16rem), 80%)', 
        left: horizontalPosition,
        right: isModalOpen ? '2rem' : 'auto',
        bottom: verticalPosition,
        transform: `translateX(${transformX})`,
        width: 'auto',
        margin: isModalOpen ? '0' : '0 8rem'
      }}
    >
      {/* Header */}
      <div className="flex items-center mr-3">
        <i className="fas fa-volume-up text-red-400 mr-2" />
        <span className="text-sm font-medium text-red-400 whitespace-nowrap">TTS Player</span>
      </div>
      
      <div className="h-4 mx-2 border-r border-neutral-700" />
      
      {/* Status indicator */}
      <div className="flex items-center mr-3">
        {(tts.isPlaying || tts.isPaused || tts.hasActive) && (
          <div className={`h-2 w-2 rounded-full mr-2 ${
            tts.isPlaying ? 'bg-green-500 animate-pulse' : 
            tts.isPaused ? 'bg-yellow-500' : 
            tts.hasActive ? 'bg-red-500 animate-pulse' : 'bg-neutral-600'}`} />
        )}
        {tts.isLoading ? (
          <span className="text-sm text-neutral-300 whitespace-nowrap">
            Loading...
          </span>
        ): tts.isInTransition && !tts.isPlaying ? (
          <span className="text-sm text-neutral-300 whitespace-nowrap">
            Switching...
          </span>
        ): tts.isPlaying ? (
          <span className="text-sm text-neutral-300 whitespace-nowrap">
            Playing{tts.source === 'text-selection' ? ' Selected Text' : ''}
          </span>
        ) : tts.isPaused ? (
          <span className="text-sm text-neutral-300 whitespace-nowrap">
            Paused
          </span>
        ) : (
          <button
            type="button"
            onClick={handleReplay}
            title='Replay'
            aria-label="Replay"
            className="flex items-center gap-1 text-sm text-green-400 hover:text-green-300 transition-colors"
          >
            <i className="fas fa-redo-alt text-md mr-1" />
          </button>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4">
        {tts.isPaused ? (
          <button
            type="button"
            onClick={tts.resume}
            className="flex items-center gap-1 text-sm text-green-400 hover:text-green-300 transition-colors"
            disabled={tts.isLoading}
            title='Resume'
            aria-label="Resume"
          >
            <i className="fas fa-play text-md mr-1" />
          </button>
        ) : (tts.isPlaying || tts.hasActive) ? (
          <button
            type="button"
            onClick={tts.pause}
            className="flex items-center gap-1 text-sm text-neutral-300 hover:text-neutral-200 transition-colors"
            disabled={tts.isLoading}
            title='Pause'
            aria-label="Pause"
          >
            <i className="fas fa-pause text-md mr-1" />
          </button>
        ) : null}
        
        <button
          type="button"
          onClick={handleStop}
          className="flex items-center gap-1 text-sm text-red-400 hover:text-red-300 transition-colors"
          disabled={false}
          title='Stop and Close'
          aria-label="Stop and Close"
        >
          <i className="fas fa-stop text-md mr-1" />
        </button>
      </div>
      
      <button
        type="button"
        onClick={handleStop}
        className="ml-3 text-neutral-400 hover:text-red-400 transition-colors"
        title="Close"
        aria-label="Close"
      >
        <i className="fas fa-times text-md" />
      </button>
    </div>,
    document.body
  );
}

export function TTSPlayer(): JSX.Element {
  const tts = useTTSStore();
  
  return (
    <TTSPlayerModal
      isVisible={tts.isVisible}
      onClose={tts.hideTTSPlayer}
      text={tts.text}
      language={tts.language}
      isPlaying={tts.isPlaying}
      isLoading={tts.isLoading}
      isPaused={tts.isPaused}
      hasActive={tts.hasActive}
      onPlay={tts.play}
      onStop={tts.stop}
      onPause={tts.pause}
      onResume={tts.resume}
    />
  );
}